import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '@/config/api';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Plus, Search, User, Phone, Mail, MessageSquare, ShoppingBag } from 'lucide-react';
import { toast } from 'sonner';

const API = API_BASE_URL;

const Customers = () => {
  const { t } = useTranslation();
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerDetail, setCustomerDetail] = useState(null);
  const [whatsappHistory, setWhatsappHistory] = useState([]);
  const [search, setSearch] = useState('');
  const [openAdd, setOpenAdd] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', email: '', address: '', notes: '' });

  const fetchCustomers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/customers`, { withCredentials: true });
      setCustomers(data);
      if (data.length > 0 && !selectedCustomer) setSelectedCustomer(data[0]);
    } catch (error) {
      console.error(error);
    }
  }, [selectedCustomer]);

  const fetchCustomerDetail = useCallback(async (id) => {
    try {
      const { data } = await axios.get(`${API}/customers/${id}`, { withCredentials: true });
      setCustomerDetail(data);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const fetchWhatsAppHistory = useCallback(async (id) => {
    try {
      const { data } = await axios.get(`${API}/customers/${id}/whatsapp-history`, { withCredentials: true });
      setWhatsappHistory(data);
    } catch (error) {
      setWhatsappHistory([]);
    }
  }, []);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  useEffect(() => {
    if (selectedCustomer) {
      fetchCustomerDetail(selectedCustomer.id);
      fetchWhatsAppHistory(selectedCustomer.id);
    }
  }, [fetchCustomerDetail, fetchWhatsAppHistory, selectedCustomer]);

  const handleAdd = async () => {
    try {
      const restaurant_id = customers[0]?.restaurant_id || 'default-restaurant';
      await axios.post(`${API}/customers`, { ...form, restaurant_id }, { withCredentials: true });
      toast.success('Customer added');
      setOpenAdd(false);
      setForm({ name: '', phone: '', email: '', address: '', notes: '' });
      fetchCustomers();
    } catch (error) {
      toast.error('Failed to add');
    }
  };

  const filteredCustomers = customers.filter(c =>
    c.name?.toLowerCase().includes(search.toLowerCase()) ||
    c.phone?.includes(search)
  );

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex items-center justify-between">
        <h1 className="text-4xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', color: '#232D4B' }}>
          {t('customers')}
        </h1>
        <Dialog open={openAdd} onOpenChange={setOpenAdd}>
          <DialogTrigger asChild>
            <Button style={{ backgroundColor: '#E57200' }} data-testid="add-customer-btn">
              <Plus className="h-4 w-4 me-2" /> {t('addCustomer')}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t('addCustomer')}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>{t('customerName')}</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="customer-name-input" /></div>
              <div><Label>{t('phone')}</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="customer-phone-input" /></div>
              <div><Label>{t('email')}</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="customer-email-input" /></div>
              <div><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
              <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            </div>
            <DialogFooter><Button onClick={handleAdd} style={{ backgroundColor: '#E57200' }} data-testid="save-customer-btn">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Customer List */}
        <Card className="border-border/40 lg:col-span-1">
          <CardHeader>
            <div className="relative">
              <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search customers..." value={search} onChange={(e) => setSearch(e.target.value)} className="ps-10" data-testid="customer-search" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-[600px] overflow-y-auto">
              {filteredCustomers.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No customers</p>
              ) : (
                filteredCustomers.map(c => (
                  <div
                    key={c.id}
                    onClick={() => setSelectedCustomer(c)}
                    className={`p-3 rounded-lg cursor-pointer transition-colors ${
                      selectedCustomer?.id === c.id ? 'bg-[#E57200]/10 border-s-4 border-[#E57200]' : 'hover:bg-muted'
                    }`}
                    data-testid={`customer-${c.id}`}
                  >
                    <div className="font-medium" style={{ fontFamily: 'Outfit, sans-serif' }}>{c.name}</div>
                    <div className="text-sm text-muted-foreground">{c.phone}</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {c.total_orders} orders • RM{c.total_spent.toFixed(2)}
                    </div>
                    {c.last_order_date && (
                      <div className="text-xs text-muted-foreground mt-0.5">
                        Last: {new Date(c.last_order_date).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Customer Detail with Tabs */}
        <Card className="border-border/40 lg:col-span-2">
          {selectedCustomer ? (
            <>
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  <User className="h-5 w-5" style={{ color: '#E57200' }} />
                  {selectedCustomer.name}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="profile">
                  <TabsList data-testid="customer-tabs">
                    <TabsTrigger value="profile" data-testid="tab-profile">Profile</TabsTrigger>
                    <TabsTrigger value="orders" data-testid="tab-orders">Order History</TabsTrigger>
                    <TabsTrigger value="whatsapp" data-testid="tab-whatsapp">WhatsApp History</TabsTrigger>
                  </TabsList>

                  <TabsContent value="profile" className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-muted-foreground" /><span>{selectedCustomer.phone}</span></div>
                      {selectedCustomer.email && (
                        <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-muted-foreground" /><span>{selectedCustomer.email}</span></div>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <Card className="bg-muted/50">
                        <CardContent className="pt-6">
                          <div className="text-xs uppercase tracking-wider text-muted-foreground">{t('totalOrders')}</div>
                          <div className="text-2xl font-bold" style={{ color: '#232D4B' }}>{selectedCustomer.total_orders}</div>
                        </CardContent>
                      </Card>
                      <Card className="bg-muted/50">
                        <CardContent className="pt-6">
                          <div className="text-xs uppercase tracking-wider text-muted-foreground">{t('totalSpent')}</div>
                          <div className="text-2xl font-bold" style={{ color: '#E57200' }}>RM{selectedCustomer.total_spent.toFixed(2)}</div>
                        </CardContent>
                      </Card>
                    </div>
                    {selectedCustomer.last_order_date && (
                      <div className="grid grid-cols-1 gap-2">
                        <div className="p-3 rounded-lg bg-muted/30">
                          <div className="text-xs uppercase tracking-wider text-muted-foreground">Last Order Date</div>
                          <div className="text-base font-medium mt-1">{new Date(selectedCustomer.last_order_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
                        </div>
                      </div>
                    )}
                    {selectedCustomer.address && (
                      <div>
                        <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Address</div>
                        <div>{selectedCustomer.address}</div>
                      </div>
                    )}
                    {selectedCustomer.notes && (
                      <div>
                        <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Notes</div>
                        <div>{selectedCustomer.notes}</div>
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="orders" className="mt-4">
                    <div className="space-y-2 max-h-[500px] overflow-y-auto">
                      {!customerDetail?.orders || customerDetail.orders.length === 0 ? (
                        <p className="text-center text-muted-foreground py-8">No orders yet</p>
                      ) : (
                        customerDetail.orders.map(order => (
                          <div key={order.id} className="flex items-center justify-between p-3 rounded-lg border border-border/40 hover:bg-muted" data-testid={`order-${order.id}`}>
                            <div className="flex items-center gap-3">
                              <ShoppingBag className="h-5 w-5" style={{ color: '#E57200' }} />
                              <div>
                                <div className="font-medium" style={{ fontFamily: 'Outfit, sans-serif' }}>{order.order_number}</div>
                                <div className="text-xs text-muted-foreground">{new Date(order.created_at).toLocaleDateString()}</div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="font-bold" style={{ color: '#E57200' }}>RM{order.total_amount.toFixed(2)}</div>
                              <Badge className="text-xs">{order.status}</Badge>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </TabsContent>

                  <TabsContent value="whatsapp" className="mt-4">
                    <div className="space-y-3 max-h-[500px] overflow-y-auto bg-muted/20 p-4 rounded-lg">
                      {whatsappHistory.length === 0 ? (
                        <div className="text-center py-8">
                          <MessageSquare className="h-12 w-12 mx-auto text-muted-foreground/50 mb-2" />
                          <p className="text-muted-foreground" style={{ fontFamily: 'Figtree, sans-serif' }}>No WhatsApp conversations yet</p>
                          <p className="text-xs text-muted-foreground mt-1">Messages will appear here once integration is connected</p>
                        </div>
                      ) : (
                        whatsappHistory.map(msg => (
                          <div key={msg.id} className={`flex ${msg.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-[70%] p-3 rounded-lg ${msg.direction === 'outbound' ? 'bg-[#E57200] text-white' : 'bg-white dark:bg-[#131B2B] border border-border'}`}>
                              <div className="text-sm">{msg.text}</div>
                              <div className={`text-xs mt-1 ${msg.direction === 'outbound' ? 'text-white/70' : 'text-muted-foreground'}`}>
                                {new Date(msg.timestamp).toLocaleString()}
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </>
          ) : (
            <CardContent className="flex items-center justify-center h-full py-20 text-muted-foreground">
              Select a customer to view details
            </CardContent>
          )}
        </Card>
      </div>
    </div>
  );
};

export default Customers;
